import { useState, useCallback } from 'react';
import type { TablePaginationConfig, SorterResult, FilterValue } from 'antd/es/table/interface';
import { UseQueryOptions, useQuery } from 'react-query';
import { PaginationProps } from '@client/screens/estimates/details/helpers/types';

interface UseTableQueryParams<TData> {
	queryKey: string | any[];
	fetcher: (params: PaginationProps) => Promise<TData>;
	defaultPageSize?: number;
	requestOptions?: Omit<UseQueryOptions<TData, unknown, TData, any>, 'queryKey' | 'queryFn'>;
}

export function useTableQuery<TData>({
	queryKey,
	fetcher,
	defaultPageSize = 100,
	requestOptions = {},
}: UseTableQueryParams<TData>) {
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: defaultPageSize,
	});
	const [filters, setFilters] = useState<Record<string, FilterValue | null>>({});
	const [sorter, setSorter] = useState<SorterResult<any> | SorterResult<any>[]>([]);
	const [search, setSearch] = useState('');

	const query = useQuery<TData>(
		[queryKey, pagination, filters, sorter, search],
		() => fetcher({ pagination, filters, sorter, search }),
		{
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			...requestOptions,
		},
	);

	const onTableChange = useCallback(
		(
			newPagination: TablePaginationConfig,
			newFilters: Record<string, FilterValue | null>,
			newSorter: SorterResult<any> | SorterResult<any>[],
		) => {
			setPagination(newPagination);
			setSorter(newSorter);

			setFilters((prev) => {
				if (Object.keys(newFilters).length === 0) {
					return prev;
				}

				return {
					...prev,
					...newFilters
				};
			});
		},
		[],
	);

	return {
		...query,
		pagination,
		filters,
		sorter,
		onTableChange,
		setSearch,
		setPagination,
	};
}
